import { Button } from "@/components/ui/baseUi/button"
import { DateInput, TimeField } from "@/components/ui/baseUi/datefield-rac"
import { AssignedCoachInput } from "@/components/ui/forms/assignedCoachInput"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Label } from "@/components/ui/baseUi/label"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useBoxPlanningActions } from "@/modules/boxPlanning/store/useBoxPlanningStore"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import type { BoxSlotDetailsEntity } from "@/modules/boxSlot/entities/boxSlotEntity"
import type { BoxSlotFormikEntity } from "@/modules/boxSlot/entities/boxSlotFormikEntity"
import type { BoxSlotInputKey } from "@/modules/boxSlot/formik/boxSlotFormikTypes"
import { useBoxSlotsDelete } from "@/modules/boxSlot/hooks/useBoxSlots"
import { useDateTimeCalendarProps } from "@/modules/boxSlot/hooks/useDateTimeCalendarProps"
import { cn } from "@/modules/lib/utils"
import type { SimpleFormik } from "@/typings/formik"
import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { Time } from "@internationalized/date"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import type { FieldInputProps } from "formik"
import { ClockIcon, LoaderCircle } from "lucide-react"
import { useEffect, useEffectEvent, useRef } from "react"

export type BoxSlotFormViewProps = {
  formik: SimpleFormik<BoxSlotFormikEntity, BoxSlotInputKey>
  hideTitle: boolean
  boxSlot?: BoxSlotDetailsEntity
  className?: string
} & React.ComponentProps<"div">

export function BoxSlotFormView({ formik, hideTitle, boxSlot, className, ...props }: BoxSlotFormViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const { hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const { validateStartDateTime } = useDateTimeCalendarProps()
  const { mutateAsync: deleteSlot } = useBoxSlotsDelete()
  const { addBoxSlotsBeingCreated } = useBoxPlanningActions()
  const { data: boxProgrammation } = useBoxProgram(formik.values.programmationId)

  const addBoxSlotsBeingCreatedEvent = useEffectEvent(() => {
    addBoxSlotsBeingCreated(
      formik.values.toDraftEntity(
        boxProgrammation.color,
        boxProgrammation.name,
        boxSlot?.id ?? `draft-${boxProgrammation.id}`,
      ),
    )
  })

  useEffect(() => {
    if (
      formik.values.endDateTime ||
      formik.values.startDateTime ||
      formik.values.participantLimit ||
      formik.values.assignedStaffMembers
    ) {
      void addBoxSlotsBeingCreatedEvent()
    }
  }, [
    formik.values.endDateTime,
    formik.values.startDateTime,
    formik.values.participantLimit,
    formik.values.assignedStaffMembers,
  ])

  const deleteSlotConfirmationPopup = () => {
    confirmPopup.show({
      title: word("sessionDetailed.deleteSession.popup.title"),
      message: word("boxSessionDetailed.deleteSession.popup.message", {
        sessionName: boxSlot?.programmationName,
      }),
      accept: async () => {
        try {
          if (boxSlot) {
            await deleteSlot(boxSlot.id)
          }
          toast.success(word("program.session.delete.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar()
        } catch {
          toast.error(word("global.error.label"), {
            classNames: errorToastClassName(),
          })
        }
      },
    })
  }

  const registrationBeginningOptions = getRegistrationBeginningOptions()
  const registrationEndOptions = getRegistrationEndOptions()
  const cancellationBeginningOptions = getCancellationBeginningOptions()

  const toggleMultiBooking = () => {
    void formik.setFieldValue("allowMultiBooking", !formik.values.allowMultiBooking)
  }

  const toggleDropIn = () => {
    void formik.setFieldValue("dropinsAccepted", !formik.values.dropinsAccepted)
  }

  const toggleTrialSessions = () => {
    void formik.setFieldValue("trialsAccepted", !formik.values.trialsAccepted)
  }

  return (
    <div className={cn("flex grow flex-col gap-4 px-5 pt-18 pb-6", className)} {...props} ref={containerRef}>
      {!hideTitle && (
        <div className="flex flex-col gap-1">
          <Text fontStyle="TL">
            {boxSlot ? word("box.slot.form.update.title") : word("box.slot.form.create.title")}
          </Text>
          <Text fontStyle="BS">{word("box.slot.form.create.description")}</Text>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <CalendarInput
          label={word("sessionForm.date.title")}
          name="date"
          isRequiredInput
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("date", formik, {
            errorMessage: word("sessionForm.error.date"),
          })}
          setFieldValue={async (field: string, value: Date) => {
            await formik.setFieldValue(field, value)
            // On date change: set startDateTime and endDateTime with correct day
            if (field === "date") {
              const newStart = dayjs(value)
                .set("hour", formik.values.startDateTime?.getHours() ?? 0)
                .set("minute", formik.values.startDateTime?.getMinutes() ?? 0)
                .set("second", 0)
                .set("millisecond", 0)
                .toDate()
              const newEnd = dayjs(value)
                .set("hour", formik.values.endDateTime?.getHours() ?? 0)
                .set("minute", formik.values.endDateTime?.getMinutes() ?? 0)
                .set("second", 0)
                .set("millisecond", 0)
                .toDate()
              await formik.setFieldValue("startDateTime", newStart)
              await formik.setFieldValue("endDateTime", newEnd)
            }
          }}
          calendarProps={{
            minDate: dayjs().subtract(1, "year").toDate(),
            maxDate: dayjs().add(1, "year").toDate(),
            dateFormat: displayedFullDateFormat,
          }}
        />

        <div className="flex gap-4">
          <TimeField
            className="w-full *:not-first:mt-2"
            aria-label={word("programmation.slotTemplates.form.hour.startTime")}
            shouldForceLeadingZeros={true}
            value={new Time(formik.values.startDateTime?.getHours(), formik.values.startDateTime?.getMinutes())}
            onChange={(e) => {
              if (e?.hour !== undefined && e?.minute !== undefined) {
                const newStartDateTime = dayjs(formik.values.startDateTime ?? undefined)
                  .set("hour", e.hour)
                  .set("minute", e.minute)
                  .set("second", 0)
                  .set("millisecond", 0)
                  .toDate()

                validateStartDateTime(newStartDateTime, formik.values.endDateTime, (val) => {
                  void formik.setFieldValue("endDateTime", val)
                })

                void formik.setFieldValue("startDateTime", newStartDateTime)
              }
            }}
            onBlur={() => {
              void formik.setFieldTouched("startDateTime" as BoxSlotInputKey, true)
            }}
          >
            <Label className="text-foreground text-xs font-medium uppercase">
              {word("programmation.slotTemplates.form.hour.startTime")}

              <Text fontStyle="LL" className="text-red-500">
                {" "}
                *
              </Text>
            </Label>
            <div className="relative">
              <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center ps-3">
                <ClockIcon size={16} aria-hidden="true" />
              </div>
              <DateInput className="ps-9" />
            </div>
            {getFormErrorMessage("startDateTime", formik)}
          </TimeField>

          <TimeField
            className="w-full *:not-first:mt-2"
            aria-label={word("programmation.slotTemplates.form.hour.endTime")}
            value={new Time(formik.values.endDateTime?.getHours(), formik.values.endDateTime?.getMinutes())}
            shouldForceLeadingZeros={true}
            onChange={(e) => {
              if (e?.hour !== undefined && e?.minute !== undefined) {
                const newEndDateTime = dayjs(formik.values.endDateTime ?? undefined)
                  .set("hour", e.hour)
                  .set("minute", e.minute)
                  .set("second", 0)
                  .set("millisecond", 0)
                  .toDate()

                void formik.setFieldValue("endDateTime", newEndDateTime)
              }
            }}
            onBlur={() => {
              void formik.setFieldTouched("endDateTime" as BoxSlotInputKey, true)
            }}
          >
            <Label className="text-foreground text-xs font-medium uppercase">
              {word("programmation.slotTemplates.form.hour.endTime")}

              <Text fontStyle="LL" className="text-red-500">
                {" "}
                *
              </Text>
            </Label>
            <div className="relative">
              <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center ps-3">
                <ClockIcon size={16} aria-hidden="true" />
              </div>
              <DateInput className="ps-9" />
            </div>
          </TimeField>
        </div>

        <StandardInput
          isRequiredInput
          label={word("programmation.slotTemplates.form.participantLimit.label")}
          getFieldProps={formik.getFieldProps}
          name="participantLimit"
          error={getFormErrorMessage("participantLimit", formik, { errorMessage: word("global.form.required") })}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            type: "number",
            inputMode: "numeric",
            min: BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
            max: BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
            step: 1,
            onChange: (e) => {
              const value = parseInt(e.target.value)
              if (value > BOX_SLOT_PARTICIPANTS_LIMIT_MAX) {
                void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MAX)
              } else if (value < BOX_SLOT_PARTICIPANTS_LIMIT_MIN) {
                void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MIN)
              } else {
                void formik.setFieldValue("participantLimit", value)
              }
            },
            onFocus: (e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false }),
          }}
        />

        <div className="flex flex-row items-center justify-between gap-2">
          {/* Drop in accepted */}
          <div
            className={cn(
              "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
              formik.values.dropinsAccepted
                ? "border border-borderPrimary"
                : "border border-borderDefault hover:border-borderDefault-hover",
            )}
            onClick={toggleDropIn}
          >
            {formik.values.dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.dropinsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.dropinsAccepted
                ? word("box.slotTemplateGroup.form.dropIn.allowed")
                : word("box.slotTemplateGroup.form.dropIn.rejected")}
            </Text>
          </div>

          {/* Trial session accepted*/}
          <div
            className={cn(
              "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
              formik.values.trialsAccepted
                ? "border border-borderPrimary"
                : "border border-borderDefault hover:border-borderDefault-hover",
            )}
            onClick={toggleTrialSessions}
          >
            {formik.values.trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.trialsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.trialsAccepted
                ? word("box.slotTemplateGroup.form.trialSession.allowed")
                : word("box.slotTemplateGroup.form.trialSession.rejected")}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.registration.label")}
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</Text>
          <div className="flex gap-2">
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitStart"
              dropdownOptions={registrationBeginningOptions}
              defaultValue={formik.values.limitStart}
              getFieldProps={() => formik.getFieldProps("limitStart") as FieldInputProps<number>}
              label={word("box.slotTemplateGroup.form.registration.beginning")}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage(`limitStart`, formik)}
            />
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitEnd"
              dropdownOptions={registrationEndOptions}
              defaultValue={formik.values.limitEnd}
              label={word("box.slotTemplateGroup.form.registration.end")}
              getFieldProps={() => formik.getFieldProps("limitEnd") as FieldInputProps<number>}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage(`limitEnd`, formik)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.cancellation.label")}
            <Text fontStyle="LL" className="text-textError">
              {" *"}
            </Text>
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</Text>
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitCancel"
            dropdownOptions={cancellationBeginningOptions}
            label={word("box.slotTemplateGroup.form.cancellation.beginning")}
            getFieldProps={() => formik.getFieldProps("limitCancel") as FieldInputProps<number>}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage(`limitCancel`, formik)}
          />
        </div>

        <div
          className={cn(
            "transition-duration-100 flex w-full cursor-pointer flex-row gap-4 rounded-3xl border-solid p-6 transition-all",
            formik.values.allowMultiBooking
              ? "border border-borderPrimary"
              : "border border-borderDefault hover:border-borderDefault-hover",
          )}
          onClick={toggleMultiBooking}
        >
          <div className="flex w-[45%] flex-col items-center gap-3">
            {formik.values.allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.allowMultiBooking ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.allowMultiBooking
                ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
                : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
            </Text>
          </div>
          <Text fontStyle="BS" className="pre-line flex w-[55%] text-textDefault-caption">
            {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
          </Text>
        </div>

        <div className="flex flex-col gap-2">
          <StandardInput
            label={word("box.slotTemplateGroup.form.area.title")}
            description={word("box.slotTemplateGroup.form.area.subtitle")}
            name="location"
            isRequiredInput={true}
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("location", formik)}
            inputProps={{
              placeholder: word("box.slotTemplateGroup.form.area.placeholder"),
            }}
          />
        </div>

        <div className="flex flex-col">
          <AssignedCoachInput
            label={word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
            textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
            name="assignedStaffMembers"
            isRequiredInput={false}
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("assignedStaffMembers", formik)}
          />
        </div>

        <div className="mt-3 flex justify-end">
          <div className="flex w-full flex-row gap-3">
            {boxSlot ? (
              <Button variant="outline" size="lg" onClick={() => deleteSlotConfirmationPopup()} className="flex-1">
                {word("global.delete")}
              </Button>
            ) : (
              <Button variant="outline" size="lg" onClick={() => hideSidebar()} className="flex-1">
                {word("global.cancel")}
              </Button>
            )}
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
                if (errorElements && errorElements.length > 0) {
                  document
                    .querySelector('[data-slot="right-sidebar-content"]')
                    ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
                }
                void formik.submitForm()
              }}
              disabled={formik.isSubmitting}
              className="flex-1"
              isLoading={formik.isSubmitting}
            >
              {formik.isSubmitting && <LoaderCircle className="animate-spin" />}
              {word("box.slot.form.submit.button")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
